import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Search, FileText, Calendar, Filter, FilterX,
  ChevronLeft, ChevronRight, Check, Copy, ChevronDown,
  ChevronUp, Play, Save, Clock, FileEdit, AlignLeft, Pencil, X,
} from 'lucide-react';
import { useQuery } from 'react-query';
import { getNotesByUserID } from 'api/notes';
import { getPatientSessionById, updatePatientSession } from 'api/patient-session';
import { getAllPatients } from 'api/patient';
import { getAllTemplates, getAllCustomTemplates } from 'api/templates';
import { getUserByCogId } from 'api/user';
import { toast } from 'react-toastify';
import Spinner from 'components/Spinner';
import { renderMarkdown } from 'components/typewriter';
import { Editor, EditorProvider, Toolbar, BtnBold, BtnItalic, BtnBulletList } from 'react-simple-wysiwyg';
import { remark } from 'remark';
import remarkHtml from 'remark-html';
import TurndownService from 'turndown';

const ITEMS_PER_PAGE = 15;

function formatTimestamp(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

function parseStructuredTranscript(text) {
  if (!text || !text.startsWith('[t=')) return null;
  const utterances = [];
  text.split('\n').forEach((line, i) => {
    const match = line.match(/^\[t=([\d.]+)\]([\s\S]*)$/);
    if (match) {
      utterances.push({ id: i, start_time: parseFloat(match[1]), text: match[2] });
    } else if (utterances.length > 0 && line.trim()) {
      // Continuation line from a multi-line utterance (pre-sanitisation data in DB)
      utterances[utterances.length - 1] = {
        ...utterances[utterances.length - 1],
        text: utterances[utterances.length - 1].text + ' ' + line.trim(),
      };
    }
  });
  return utterances.length > 0 ? utterances : null;
}

function stripTimestampMarkers(text) {
  if (!text) return text;
  return text.replace(/^\[t=[\d.]+\]/gm, '').trim();
}

const _turndown = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-', codeBlockStyle: 'fenced' });

const convertMarkdownToHtml = (markdown) => {
  try {
    return remark().use(remarkHtml, { sanitize: false }).processSync(markdown).toString();
  } catch {
    return markdown;
  }
};

const convertHtmlToMarkdown = (html) => {
  try {
    return _turndown.turndown(html);
  } catch {
    return html;
  }
};

const PatientNameInput = ({ value, onChange, onSave, onCancel, isSaving }) => {
  const [patients, setPatients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      setLoadingPatients(true);
      try {
        const data = await getAllPatients();
        const unique = data.reduce((acc, p) => {
          if (p.firstName !== 'Untitled' && !acc.find(x => x.firstName === p.firstName)) {
            acc.push({ id: p.id, firstName: p.firstName });
          }
          return acc;
        }, []);
        setPatients(unique);
        setFiltered(unique);
      } catch {
      } finally {
        setLoadingPatients(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const v = e.target.value;
    onChange(v);
    const f = v ? patients.filter(p => p.firstName.toLowerCase().includes(v.toLowerCase())) : patients;
    setFiltered(f);
    setSelectedIndex(-1);
    if (!isOpen) setIsOpen(true);
  };

  const handleSelect = (name) => {
    onChange(name);
    setIsOpen(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown') { setIsOpen(true); return; }
      if (e.key === 'Enter') { onSave(); return; }
      if (e.key === 'Escape') { onCancel(); return; }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < filtered.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : filtered.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filtered.length) {
          handleSelect(filtered[selectedIndex].firstName);
        } else {
          onSave();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="flex items-center gap-1.5" ref={containerRef}>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onFocus={() => {
            setIsOpen(true);
            setFiltered(value ? patients.filter(p => p.firstName.toLowerCase().includes(value.toLowerCase())) : patients);
          }}
          onKeyDown={handleKeyDown}
          autoFocus
          autoComplete="off"
          placeholder="Patient name"
          className="text-sm font-semibold border border-primary rounded-lg px-2 py-0.5 focus:outline-none focus:ring-2 focus:ring-primary/40 w-48"
        />
        {isOpen && (
          <div className="absolute z-20 top-full mt-1 left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {loadingPatients ? (
              <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">No matching patients</div>
            ) : (
              filtered.map((p, idx) => (
                <button
                  key={p.id}
                  type="button"
                  onMouseDown={e => { e.preventDefault(); handleSelect(p.firstName); }}
                  onMouseEnter={() => setSelectedIndex(idx)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                    idx === selectedIndex ? 'bg-primary/10 text-gray-900' : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {p.firstName}
                </button>
              ))
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving || !value.trim()}
        className="flex items-center justify-center w-6 h-6 rounded-md bg-primary text-black hover:bg-primary/80 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0"
        title="Save name"
      >
        {isSaving ? <Spinner size={10} /> : <Check className="h-3.5 w-3.5" />}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all flex-shrink-0"
        title="Cancel"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

const SessionHistoryPanel = ({ onContinueSession, isRecording, hasActiveSession }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateFiltered, setDateFiltered] = useState(false);
  const [currentFromDate, setCurrentFromDate] = useState('');
  const [currentToDate, setCurrentToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [expandedId, setExpandedId] = useState(null);
  const [activeTemplateTab, setActiveTemplateTab] = useState({});
  const [contentTab, setContentTab] = useState({});
  // sessionDetails[sessionId] = { transcribedText, duration, notes: [{...note, templateId}] }
  const [sessionDetails, setSessionDetails] = useState({});
  const [fetchingId, setFetchingId] = useState(null);

  const [editedNotes, setEditedNotes] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [copiedTranscriptKey, setCopiedTranscriptKey] = useState(null); // "<sessionId>_ts" | "<sessionId>_plain"
  const [continuingId, setContinuingId] = useState(null);
  const [editingNoteKey, setEditingNoteKey] = useState(null);
  const [editorHtmlValue, setEditorHtmlValue] = useState('');

  const [editingNameId, setEditingNameId] = useState(null);
  const [nameInputValue, setNameInputValue] = useState('');
  const [savingNameId, setSavingNameId] = useState(null);
  const [editedPatientNames, setEditedPatientNames] = useState({});

  const { data: currentUser } = useQuery('currentUser', getUserByCogId);
  const userId = currentUser?.cognitoId;

  // Fetch all templates (standard + custom) to build name→id map
  const { data: standardTemplates = [] } = useQuery('allTemplates', getAllTemplates);
  const { data: customTemplates = [] } = useQuery('allCustomTemplates', getAllCustomTemplates);

  const templateNameToId = useMemo(() => {
    const map = {};
    [...standardTemplates, ...customTemplates].forEach(t => { map[t.name] = t.id; });
    return map;
  }, [standardTemplates, customTemplates]);

  const fetchNotes = useCallback(async (from = null, to = null) => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getNotesByUserID(userId, from, to);
      setNotes(data || []);
      setCurrentPage(1);
      setCurrentFromDate(from ? from.split('T')[0] : '');
      setCurrentToDate(to ? to.split('T')[0] : '');
    } catch {
      setNotes([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { if (userId) fetchNotes(); }, [userId, fetchNotes]);

  const groupedSessions = useMemo(() => {
    if (!notes.length) return [];
    const map = notes.reduce((acc, note) => {
      const sid = note.sessionId;
      if (!acc[sid]) acc[sid] = { sessionId: sid, createdAt: note.createdAt, firstName: note.firstName, notes: [] };
      acc[sid].notes.push(note);
      return acc;
    }, {});
    return Object.values(map).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [notes]);

  const filteredSessions = useMemo(() => {
    if (!searchTerm) return groupedSessions;
    return groupedSessions.filter(s => s.firstName.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [groupedSessions, searchTerm]);

  const totalPages = Math.ceil(filteredSessions.length / ITEMS_PER_PAGE);
  const pageSessions = filteredSessions.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const formatDuration = (s) => {
    if (!s) return '—';
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? `${h}h ${m}m ${sec}s` : m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const handleApplyFilter = () => {
    if (!fromDate || !toDate) return;
    fetchNotes(new Date(fromDate).toISOString(), new Date(toDate + 'T23:59:59').toISOString());
    setDateFiltered(true);
  };

  const handleClearFilter = () => {
    setFromDate(''); setToDate(''); setSearchTerm('');
    setDateFiltered(false); setCurrentFromDate(''); setCurrentToDate('');
    fetchNotes();
  };

  // Build enriched notes (with templateId from name map) for a session's raw notes
  const enrichNotes = useCallback((rawNotes) => {
    return rawNotes.map(n => ({
      ...n,
      templateId: templateNameToId[n.TemplateName] ?? null,
    }));
  }, [templateNameToId]);

  // Fetch session details on expand (only getPatientSessionById — avoids the broken /notes/session endpoint)
  const fetchSessionDetail = useCallback(async (session) => {
    if (sessionDetails[session.sessionId]) return sessionDetails[session.sessionId];
    setFetchingId(session.sessionId);
    try {
      const sessionData = await getPatientSessionById(session.sessionId);
      const detail = {
        transcribedText: sessionData.transcribedText || '',
        duration: sessionData.duration || 0,
        notes: enrichNotes(session.notes),
      };
      setSessionDetails(prev => ({ ...prev, [session.sessionId]: detail }));
      setActiveTemplateTab(prev => ({
        ...prev,
        [session.sessionId]: prev[session.sessionId] ?? detail.notes[0]?.templateId ?? null,
      }));
      setContentTab(prev => ({ ...prev, [session.sessionId]: prev[session.sessionId] ?? 'notes' }));
      return detail;
    } catch {
      toast.error('Failed to load session details');
      return null;
    } finally {
      setFetchingId(null);
    }
  }, [sessionDetails, enrichNotes]);

  const handleExpandRow = async (session) => {
    if (expandedId === session.sessionId) { setExpandedId(null); return; }
    setExpandedId(session.sessionId);
    await fetchSessionDetail(session);
  };

  const getActiveNoteContent = (sessionId) => {
    const detail = sessionDetails[sessionId];
    if (!detail) return '';
    const templateId = activeTemplateTab[sessionId];
    const editKey = `${sessionId}_${templateId}`;
    if (editedNotes[editKey] !== undefined) return editedNotes[editKey];
    return detail.notes.find(n => n.templateId === templateId)?.notesContent || '';
  };

  const hasUnsavedChanges = (sessionId) => {
    const detail = sessionDetails[sessionId];
    if (!detail) return false;
    return detail.notes.some(n => editedNotes[`${sessionId}_${n.templateId}`] !== undefined);
  };

  const handleSaveChanges = async (session) => {
    const detail = sessionDetails[session.sessionId];
    if (!detail) return;
    const notesWithTemplateId = detail.notes.filter(n => n.templateId);
    if (!notesWithTemplateId.length) { toast.error('Cannot save — template IDs could not be resolved.'); return; }

    setSavingId(session.sessionId);
    try {
      const updatedNotes = notesWithTemplateId.map(n => ({
        notesContent: editedNotes[`${session.sessionId}_${n.templateId}`] ?? n.notesContent,
        patientSessionId: parseInt(session.sessionId),
        templateId: parseInt(n.templateId),
      }));
      await updatePatientSession(session.sessionId, {
        notes: updatedNotes,
        duration: detail.duration || 0,
        transcribedText: detail.transcribedText || '',
        firstName: editedPatientNames[session.sessionId] ?? session.firstName,
        recommendation: '',
      });
      setSessionDetails(prev => ({
        ...prev,
        [session.sessionId]: {
          ...prev[session.sessionId],
          notes: prev[session.sessionId].notes.map(n => ({
            ...n,
            notesContent: editedNotes[`${session.sessionId}_${n.templateId}`] ?? n.notesContent,
          })),
        },
      }));
      setEditedNotes(prev => {
        const next = { ...prev };
        detail.notes.forEach(n => delete next[`${session.sessionId}_${n.templateId}`]);
        return next;
      });
      toast.success('Notes saved');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSavingId(null);
    }
  };

  const handleSaveName = async (session) => {
    const newName = nameInputValue.trim();
    if (!newName) return;
    const detail = sessionDetails[session.sessionId];
    if (!detail) return;
    const notesWithTemplateId = detail.notes.filter(n => n.templateId);
    if (!notesWithTemplateId.length) {
      toast.error('Cannot rename — template IDs could not be resolved.');
      return;
    }
    setSavingNameId(session.sessionId);
    try {
      await updatePatientSession(session.sessionId, {
        notes: notesWithTemplateId.map(n => ({
          notesContent: editedNotes[`${session.sessionId}_${n.templateId}`] ?? n.notesContent,
          patientSessionId: parseInt(session.sessionId),
          templateId: parseInt(n.templateId),
        })),
        duration: detail.duration || 0,
        transcribedText: detail.transcribedText || '',
        firstName: newName,
        recommendation: '',
      });
      setEditedPatientNames(prev => ({ ...prev, [session.sessionId]: newName }));
      setEditingNameId(null);
      toast.success('Patient name updated');
    } catch {
      toast.error('Failed to update patient name');
    } finally {
      setSavingNameId(null);
    }
  };

  const handleCopy = async (sessionId) => {
    const content = getActiveNoteContent(sessionId);
    if (!content) return;

    const formatForHTML = (text) => {
      let formatted = text;
      formatted = formatted.replace(/\n\n/g, '\n');
      formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
      formatted = formatted.replace(/\n/g, '<br>');
      return `<p>${formatted}</p>`;
    };

    const formatForPlainText = (text) => {
      let formatted = text;
      formatted = formatted.replace(/\*\*(.*?)\*\*/g, '$1');
      formatted = formatted.replace(/\*(.*?)\*/g, '$1');
      formatted = formatted.replace(/\n\n/g, '\n');
      formatted = formatted.replace(/[ \t]+\n/g, '\n');
      formatted = formatted.replace(/\n[ \t]+/g, '\n');
      return formatted.trim();
    };

    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([formatForHTML(content)], { type: 'text/html' }),
          'text/plain': new Blob([formatForPlainText(content)], { type: 'text/plain' }),
        }),
      ]);
    } catch {
      await navigator.clipboard.writeText(formatForPlainText(content));
    }
    const key = `${sessionId}_${activeTemplateTab[sessionId]}`;
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const handleCopyTranscript = async (sessionId, withTimestamps) => {
    const detail = sessionDetails[sessionId];
    if (!detail?.transcribedText) return;

    let text;
    if (withTimestamps) {
      const parsed = parseStructuredTranscript(detail.transcribedText);
      if (parsed) {
        text = parsed.map(u => `${formatTimestamp(u.start_time)} ·\n${u.text}`).join('\n\n');
      } else {
        text = detail.transcribedText;
      }
    } else {
      text = stripTimestampMarkers(detail.transcribedText) || detail.transcribedText;
    }

    try { await navigator.clipboard.writeText(text); } catch { return; }
    const key = `${sessionId}_${withTimestamps ? 'ts' : 'plain'}`;
    setCopiedTranscriptKey(key);
    setTimeout(() => setCopiedTranscriptKey(null), 2000);
  };

  // Continue: fetch detail on demand if not loaded, then call parent handler
  const handleContinueSession = async (session) => {
    setContinuingId(session.sessionId);
    try {
      let detail = sessionDetails[session.sessionId];
      if (!detail) {
        detail = await fetchSessionDetail(session);
        if (!detail) return;
      }
      const notesWithTemplateId = detail.notes.filter(n => n.templateId);
      console.log('[Continue Session]', {
        sessionId: session.sessionId,
        patientName: session.firstName,
        duration: detail.duration,
        transcribedText: detail.transcribedText?.slice(0, 200),
        notes: notesWithTemplateId.map(n => ({ templateId: n.templateId, templateName: n.TemplateName, contentLength: n.notesContent?.length })),
      });
      await onContinueSession({
        sessionId: session.sessionId,
        patientName: session.firstName,
        duration: detail.duration,
        transcribedText: detail.transcribedText,
        sessionNotes: notesWithTemplateId,
      });
    } catch {
      toast.error('Failed to continue session');
    } finally {
      setContinuingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2">
        <Spinner size={28} />
        <span className="text-sm text-gray-500">Loading sessions…</span>
      </div>
    );
  }

  return (
    <div className="animate-fade-up">
      <p className="text-sm text-gray-500 mb-4">View, edit, and continue your past patient sessions.</p>

      {/* Filters */}
      <div className="mb-5 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Search Patient</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input type="text" placeholder="Search by name…" value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">From</label>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">To</label>
            <input type="date" value={toDate} min={fromDate} onChange={e => setToDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary" />
          </div>
          <div className="flex gap-2 items-end">
            <button onClick={handleApplyFilter} disabled={!fromDate || !toDate}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-gray-800 rounded-lg hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95">
              <Filter className="h-4 w-4" /> Apply
            </button>
            <button onClick={handleClearFilter} title="Clear"
              className="px-3 py-2 text-gray-600 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-all active:scale-95">
              <FilterX className="h-4 w-4" />
            </button>
          </div>
        </div>
        {dateFiltered && currentFromDate && currentToDate && (
          <div className="mt-3 flex items-center gap-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <Filter className="h-3.5 w-3.5 flex-shrink-0" />
            Showing {new Date(currentFromDate).toLocaleDateString()} – {new Date(currentToDate).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* Session list */}
      <div className="space-y-2">
        {pageSessions.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <FileText className="mx-auto h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm font-medium text-gray-500">No sessions found</p>
            <p className="text-xs text-gray-400 mt-1">
              {searchTerm || dateFiltered ? 'Try adjusting your search or date filter.' : 'Your completed sessions will appear here.'}
            </p>
          </div>
        )}

        {pageSessions.map((session) => {
          const isExpanded = expandedId === session.sessionId;
          const detail = sessionDetails[session.sessionId];
          const isFetching = fetchingId === session.sessionId;
          const isContinuing = continuingId === session.sessionId;
          const isSaving = savingId === session.sessionId;
          const dirty = hasUnsavedChanges(session.sessionId);
          const activeTab = contentTab[session.sessionId] || 'notes';
          const activeTemplId = activeTemplateTab[session.sessionId];
          const editKey = `${session.sessionId}_${activeTemplId}`;
          const isThisEditing = editingNoteKey === editKey;

          return (
            <div key={session.sessionId}
              className={`rounded-xl border transition-all duration-200 overflow-hidden ${
                isExpanded
                  ? 'bg-white border-primary shadow-lg ring-1 ring-primary/30'
                  : 'bg-white border-gray-200 shadow-sm hover:border-gray-300 hover:shadow'
              }`}>

              {/* Collapsed row */}
              <div
                className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer select-none transition-colors ${
                  isExpanded ? 'bg-cyan-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => handleExpandRow(session)}>
                <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900">{editedPatientNames[session.sessionId] ?? session.firstName}</span>
                    <span className="text-xs text-gray-400">{formatDate(session.createdAt)}</span>
                    <div className="flex flex-wrap gap-1">
                      {session.notes.map(n => (
                        <span key={n.noteId}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-md ${
                            isExpanded ? 'bg-primary/30 text-cyan-900' : 'bg-gray-100 text-gray-600'
                          }`}>
                          <FileText className="h-3 w-3" />{n.TemplateName}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {isRecording && (
                    <span className="text-[10px] text-amber-600 font-medium hidden sm:inline">Stop recording first</span>
                  )}
                  {!isRecording && hasActiveSession && (
                    <span className="text-[10px] text-amber-600 font-medium hidden sm:inline">Finish current session first</span>
                  )}
                  <button
                    onClick={e => { e.stopPropagation(); if (!isRecording && !hasActiveSession && !isContinuing) handleContinueSession(session); }}
                    disabled={isRecording || hasActiveSession || isContinuing}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-gray-900 text-white rounded-lg hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95">
                    {isContinuing ? <Spinner size={12} /> : <Play className="h-3 w-3 fill-white" />}
                    Continue
                  </button>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="border-t border-primary/20 bg-white">
                  {isFetching && (
                    <div className="flex items-center justify-center gap-2 py-8 text-sm text-gray-400">
                      <Spinner size={18} /> Loading session…
                    </div>
                  )}

                  {!isFetching && detail && (
                    <div className="p-4 space-y-4">
                      {/* Patient name edit row */}
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-gray-500 flex-shrink-0">Patient:</span>
                        {editingNameId === session.sessionId ? (
                          <PatientNameInput
                            value={nameInputValue}
                            onChange={setNameInputValue}
                            onSave={() => handleSaveName(session)}
                            onCancel={() => setEditingNameId(null)}
                            isSaving={savingNameId === session.sessionId}
                          />
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-semibold text-gray-900">
                              {editedPatientNames[session.sessionId] ?? session.firstName}
                            </span>
                            <button
                              onClick={() => {
                                setNameInputValue(editedPatientNames[session.sessionId] ?? session.firstName);
                                setEditingNameId(session.sessionId);
                              }}
                              className="flex items-center justify-center w-6 h-6 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
                              title="Edit patient name"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Meta */}
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5">
                          <Clock className="h-3.5 w-3.5" />
                          Duration: <strong>{formatDuration(detail.duration)}</strong>
                        </span>
                        <span className="flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5" />
                          {detail.notes.length} note{detail.notes.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {/* Notes / Transcript tabs */}
                      <div className="flex items-center gap-1 bg-gray-200 rounded-lg p-1 w-fit">
                        {['notes', 'transcript'].map(tab => (
                          <button key={tab}
                            onClick={() => setContentTab(prev => ({ ...prev, [session.sessionId]: tab }))}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                              activeTab === tab ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}>
                            {tab === 'notes' ? <FileEdit className="h-3.5 w-3.5" /> : <AlignLeft className="h-3.5 w-3.5" />}
                            {tab === 'notes' ? 'Notes' : 'Transcript'}
                          </button>
                        ))}
                      </div>

                      {/* Notes tab */}
                      {activeTab === 'notes' && (
                        <div className="space-y-3">
                          {/* Template tabs */}
                          {detail.notes.length > 1 && (
                            <div className="flex flex-wrap gap-1.5">
                              {detail.notes.map(n => (
                                <button key={n.templateId ?? n.noteId}
                                  onClick={() => {
                                    setActiveTemplateTab(prev => ({ ...prev, [session.sessionId]: n.templateId }));
                                    setEditingNoteKey(null);
                                  }}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                    activeTemplId === n.templateId
                                      ? 'bg-primary text-black shadow-sm'
                                      : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                                  }`}>
                                  {n.TemplateName}
                                  {editedNotes[`${session.sessionId}_${n.templateId}`] !== undefined && (
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                                  )}
                                </button>
                              ))}
                            </div>
                          )}

                          {/* Note — rendered view by default; WYSIWYG editor on click */}
                          <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                            <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                              <span className="text-xs font-semibold text-gray-600">
                                {detail.notes.find(n => n.templateId === activeTemplId)?.TemplateName || 'Note'}
                              </span>
                              {isThisEditing ? (
                                <span className="text-[10px] text-cyan-600 font-medium">Editing — click outside to finish</span>
                              ) : (
                                <span className="text-[10px] text-gray-400">Click to edit</span>
                              )}
                            </div>
                            {isThisEditing ? (
                              <div
                                onBlur={(e) => {
                                  if (!e.currentTarget.contains(e.relatedTarget)) {
                                    setEditingNoteKey(null);
                                  }
                                }}
                              >
                                <EditorProvider>
                                  <Editor
                                    value={editorHtmlValue}
                                    onChange={(e) => {
                                      const html = e.target.value;
                                      setEditorHtmlValue(html);
                                      setEditedNotes(prev => ({ ...prev, [editKey]: convertHtmlToMarkdown(html) }));
                                    }}
                                  >
                                    <Toolbar>
                                      <BtnBold />
                                      <BtnItalic />
                                      <BtnBulletList />
                                    </Toolbar>
                                  </Editor>
                                </EditorProvider>
                              </div>
                            ) : (
                              <div
                                onClick={() => {
                                  const content = getActiveNoteContent(session.sessionId);
                                  setEditorHtmlValue(convertMarkdownToHtml(content));
                                  setEditingNoteKey(editKey);
                                }}
                                className="w-full p-4 text-sm text-gray-800 min-h-[200px] cursor-text leading-relaxed"
                              >
                                {getActiveNoteContent(session.sessionId)
                                  ? renderMarkdown(getActiveNoteContent(session.sessionId))
                                  : <span className="text-gray-400 italic">No notes content…</span>
                                }
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex items-center justify-between">
                            <div className="flex gap-2">
                              <button onClick={() => handleCopy(session.sessionId)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all active:scale-95 ${
                                  copiedKey === `${session.sessionId}_${activeTemplId}`
                                    ? 'bg-green-50 text-green-700 border-green-300'
                                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                                }`}>
                                {copiedKey === `${session.sessionId}_${activeTemplId}`
                                  ? <><Check className="h-3.5 w-3.5" /> Copied!</>
                                  : <><Copy className="h-3.5 w-3.5" /> Copy</>}
                              </button>
                              {dirty && (
                                <button onClick={() => handleSaveChanges(session)} disabled={isSaving}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-all active:scale-95">
                                  {isSaving ? <Spinner size={12} /> : <Save className="h-3.5 w-3.5" />}
                                  Save Changes
                                </button>
                              )}
                            </div>
                            <button onClick={() => handleContinueSession(session)}
                              disabled={isRecording || hasActiveSession || isContinuing}
                              title={isRecording ? "Stop recording first" : hasActiveSession ? "Finish your current session first" : undefined}
                              className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-gray-900 text-white rounded-lg hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95">
                              {isContinuing ? <Spinner size={12} /> : <Play className="h-3.5 w-3.5 fill-white" />}
                              Continue Session
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Transcript tab */}
                      {activeTab === 'transcript' && (() => {
                        const parsed = parseStructuredTranscript(detail.transcribedText);
                        const tsKey = `${session.sessionId}_ts`;
                        const plainKey = `${session.sessionId}_plain`;
                        return (
                          <div className="space-y-3">
                            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                              <div className="px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                                <span className="text-xs font-semibold text-gray-500">Transcript</span>
                                {detail.transcribedText && (
                                  <div className="flex items-stretch gap-px rounded-lg overflow-hidden border border-gray-300">
                                    <button
                                      onClick={() => handleCopyTranscript(session.sessionId, true)}
                                      className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                                        copiedTranscriptKey === tsKey
                                          ? 'bg-green-50 text-green-700'
                                          : 'bg-white text-gray-600 hover:bg-gray-50'
                                      }`}
                                    >
                                      {copiedTranscriptKey === tsKey
                                        ? <><Check className="h-3 w-3" /> Copied</>
                                        : <><Copy className="h-3 w-3" /> With timestamps</>}
                                    </button>
                                    <div className="w-px bg-gray-300" />
                                    <button
                                      onClick={() => handleCopyTranscript(session.sessionId, false)}
                                      className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                                        copiedTranscriptKey === plainKey
                                          ? 'bg-green-50 text-green-700'
                                          : 'bg-white text-gray-600 hover:bg-gray-50'
                                      }`}
                                    >
                                      {copiedTranscriptKey === plainKey
                                        ? <><Check className="h-3 w-3" /> Copied</>
                                        : <><Copy className="h-3 w-3" /> Without timestamps</>}
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="p-4 max-h-72 overflow-y-auto">
                                {detail.transcribedText ? (
                                  parsed ? (
                                    <div className="space-y-3">
                                      {parsed.map(u => (
                                        <div key={u.id}>
                                          <div className="flex items-center gap-1 mb-0.5">
                                            <span className="text-[11px] font-bold tracking-widest text-slate-500">{formatTimestamp(u.start_time)}</span>
                                            <span className="font-black text-sm text-slate-500 leading-none select-none"> ·</span>
                                          </div>
                                          <p className="text-sm text-gray-700 leading-relaxed">{u.text}</p>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                      {detail.transcribedText}
                                    </p>
                                  )
                                ) : (
                                  <p className="text-sm text-gray-400 italic">No transcript saved for this session.</p>
                                )}
                              </div>
                            </div>
                            <div className="flex justify-end">
                              <button onClick={() => handleContinueSession(session)}
                                disabled={isRecording || hasActiveSession || isContinuing}
                                title={isRecording ? "Stop recording first" : hasActiveSession ? "Finish your current session first" : undefined}
                                className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold bg-gray-900 text-white rounded-lg hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-all active:scale-95">
                                {isContinuing ? <Spinner size={12} /> : <Play className="h-3.5 w-3.5 fill-white" />}
                                Continue Session
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-5 flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm">
          <p className="text-xs text-gray-500">
            {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, filteredSessions.length)} of {filteredSessions.length}
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-all">
              <ChevronLeft className="h-4 w-4" />
            </button>
            {[...Array(totalPages)].map((_, i) => {
              const p = i + 1;
              if (p === 1 || p === totalPages || (p >= currentPage - 1 && p <= currentPage + 1)) {
                return (
                  <button key={p} onClick={() => setCurrentPage(p)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition-all ${
                      currentPage === p ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-100'
                    }`}>{p}</button>
                );
              }
              if (p === currentPage - 2 || p === currentPage + 2) {
                return <span key={p} className="text-gray-400 text-xs px-1">…</span>;
              }
              return null;
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-all">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionHistoryPanel;
